/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AdcsApiService, SecurityApiService } from '../../../shared/http';
import { AdcsUtilsService, NamespaceService } from '../../../shared/services/utils';
import { BaseComponent } from '../../../../../core/components';
import { Component } from '@angular/core';
import { ErrorResponseModel } from '../../../../../core/models';
import { LoadingService, LogService, RbacService, UserService } from '../../../../../core/services';
import { Router } from '@angular/router';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-insights',
    templateUrl: './insights.component.html',
    styleUrls: ['./insights.component.scss']
})
export class InsightsComponent extends BaseComponent {
    /**
     * Has tier
     */
    public hasTier: boolean = false;

    /**
     * Namespace selected key.
     */
    public selectedNamespaceKey: number;

    /**
     * Table row data.
     */
    public tableRows: any[] = [];

    /**
     * Search term for searching the services table, also used with the search query parameter in the url.
     */
    public searchTerm: string | [] = '';

    /**
     * Matched keys coming from search component.
     */
    public matchedKeys = [];

    /**
     * Store the user's ability to edit
     */
    public userCanEdit: boolean = false;

    /**
     * Store permissions in lookup table.
     */
    public permissionsLookup: any = {};

    /**
     * Flag on loading state.
     */
    public isLoading: boolean = true;

    constructor(
        public adcUtilsService: AdcsUtilsService,
        private adcApiService: AdcsApiService,
        private loadingService: LoadingService,
        private logService: LogService,
        private namespaceService: NamespaceService,
        private rbacService: RbacService,
        private router: Router,
        private securityApiService: SecurityApiService,
        private userService: UserService
    ) {
        super();

        this.hasTier = this.rbacService.getPageRequiredRoute('/api/v1/:namespace/security/insights')?.tier;
        this.selectedNamespaceKey = this.namespaceService.selectedNamespace.key;
        this.setupUserValues();
        this.setupSubscribers();
        this.setUpPageData();
        this.registerButtons();
    }

    /**
     * Get the matched search keys from the search component and store them.
     * @param $event
     */
    public matchedSearchKeys($event: any[]): void {
        this.matchedKeys = $event;
    }

    /**
     * Update ADC.
     * @param id
     */
    public systemUpdateAdc(id: number): void {
        this.adcApiService.systemUpdate(this.selectedNamespaceKey, id)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (): void => {
                    this.router.navigate(['/cm/adcs/list']).then().catch();
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('SecurityListComponent: systemUpdateAdc', 'Error submitting data', error);
                }
            }).add(() => this.loadingService.loadingActionDotsEmitter.next(false));
    }

    /**
     * Set up user values.
     * @private
     */
    private setupUserValues(): void {
        const user = this.userService.getUser();
        this.userCanEdit = user.permissions && (user.permissions < 4 && user.permissions !== 0);
    }

    /**
     * Set up the page data.
     * @private
     */
    private setUpPageData(): void {
        this.securityApiService.getAllSecurity(this.selectedNamespaceKey)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: any[]): void => {
                    this.populateTableRows(data);
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error(
                        'SecurityListComponent:setUpPageData',
                        'Unable to get page data. ' + error.message
                    );
                }
            });
    }

    /**
     * Populate table rows.
     * @private
     */
    private populateTableRows(data: any): void {
        this.tableRows = [];

        for (const item of data) {
            this.tableRows.push({
                id: item.adcId,
                label: item.adcLabel,
                vendor: item.vendor,
                securityRisk: item.securityRisk,
                operationalRisk: item.operationalRisk?.toLowerCase(),
                recommendation: this.adcUtilsService.getRecommendationText(item.operationalRisk)
            });
        }

        this.isLoading = false;
    }

    /**
     * Setup page subscribers.
     * @private
     */
    private setupSubscribers(): void {
        this.namespaceService.selectedEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (namespace: any): void => {
                    this.selectedNamespaceKey = namespace.key;
                    this.setUpPageData();
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('AdcsListComponent: setupSubscribers', 'Unable to get selected namespace', error);
                }
            });
    }

    /**
     * Register the page's buttons for RBAC.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'security-update-adc': [{ path: '/api/v1/:namespace/adcs/:id/updates', methods: ['PUT'] }],
            'security-more-details': [{ path: '/api/v1/:namespace/security/insights/:adcid', methods: ['GET'] }]
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
    }
}
